import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ExternalLink, ArrowRight, TrendingUp, BadgeCheck, GitCompare, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useTools } from '../../hooks/useData';
import { useCompare } from '../../context/CompareContext';
import { toast } from 'sonner';

const TrendingTools = () => {
  const { tools, loading } = useTools();
  const trendingTools = tools.filter((tool) => tool.trending).slice(0, 8);
  const { toggleCompare, isInCompare, compareCount } = useCompare();

  const handleToggleCompare = (e, tool) => {
    e.preventDefault();
    e.stopPropagation();
    const result = toggleCompare(tool);
    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  };

  if (loading) {
    return (
      <section className="py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            <span className="ml-2 text-gray-600">Loading tools...</span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-4">
              <TrendingUp className="w-4 h-4" />
              Trending Now
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Popular Tools This Month
            </h2>
            <p className="text-lg text-gray-600 max-w-xl">
              Discover the most sought-after software tools based on user engagement and reviews
            </p>
          </div>
          <div className="flex items-center gap-4">
            {compareCount > 0 && (
              <Link to="/compare">
                <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="home-compare-btn">
                  <GitCompare className="w-4 h-4 mr-2" />
                  Compare ({compareCount})
                </Button>
              </Link>
            )}
            <Link
              to="/tools"
              className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium group"
            >
              Explore all tools
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {trendingTools.map((tool, index) => (
            <div
              key={tool.id}
              className="group bg-white rounded-2xl border border-gray-100 p-5 hover:border-gray-200 hover:shadow-xl hover:shadow-gray-100/50 transition-all duration-300 relative"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <Link to={`/tool/${tool.slug}`}>
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gray-100 overflow-hidden">
                    <img
                      src={tool.logo}
                      alt={tool.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/100?text=' + tool.name.charAt(0);
                      }}
                    />
                  </div>
                  {tool.verified && (
                    <Badge variant="secondary" className="bg-emerald-50 text-emerald-600 border-0">
                      <BadgeCheck className="w-3 h-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                </div>

                {/* Content */}
                <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-emerald-600 transition-colors">
                  {tool.name}
                </h3>
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                  {tool.description}
                </p>

                {/* Category */}
                <div className="mb-4">
                  <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-md">
                    {tool.category}
                  </span>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    <span className="text-sm font-medium text-gray-900">{tool.rating}</span>
                    <span className="text-xs text-gray-400">({(tool.reviews || 0).toLocaleString()})</span>
                  </div>
                  <span className="text-sm font-medium text-emerald-600">
                    {tool.pricing}
                  </span>
                </div>
              </Link>

              {/* Add to Compare Button */}
              <button
                onClick={(e) => handleToggleCompare(e, tool)}
                className={`absolute top-4 right-4 p-2 rounded-lg transition-all ${
                  isInCompare(tool.id)
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-emerald-100 hover:text-emerald-600 opacity-0 group-hover:opacity-100'
                }`}
                title={isInCompare(tool.id) ? 'Remove from Compare' : 'Add to Compare'}
                data-testid={`home-compare-btn-${tool.slug}`}
              >
                {isInCompare(tool.id) ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <GitCompare className="w-4 h-4" />
                )}
              </button>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Button
            asChild
            variant="outline"
            size="lg"
            className="border-gray-200 hover:bg-gray-50"
          >
            <Link to="/tools">
              View All Tools
              <ExternalLink className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TrendingTools;
